import { useSelector } from 'react-redux'
import { createSelector } from 'reselect'
import { ILoad, ILoadDetailsState } from 'apps/fleet-planner/types'
import { IStore } from 'apps/fleet-planner/utils/types/store'

export const loadDetailsSelector = (state: IStore) => state.loadDetails
const loadDataSelector = createSelector(loadDetailsSelector, ({ load }) => load)
const firstPickupSelector = createSelector(loadDataSelector, load => load?.stops[0] || {})

const lastDeliverySelector = createSelector(
  loadDataSelector,
  load => load?.stops[load?.stops.length - 1] || {}
)

export const loadOverviewSelector = createSelector(
  loadDataSelector,
  firstPickupSelector,
  lastDeliverySelector,
  (
    { customer },
    { city: pickupCity, stateCode: pickupStateCode },
    { city: deliveryCity, stateCode: deliveryStateCode }
  ) => ({
    customer,
    pickupCity,
    pickupStateCode,
    deliveryCity,
    deliveryStateCode,
  })
)

export const customerNotesSelector = createSelector(
  loadDetailsSelector,
  res => res?.load?.customerNotes
)

/**
 * Allows to dynamically select only parts of a selector's return
 * @example const selector = createLoadSelector('isExternal')
 */
export const createLoadSelector = <K extends keyof ILoad>(keyName: K) => {
  return createSelector<IStore, ILoadDetailsState, Record<K, ILoad[K]>>(
    loadDetailsSelector,
    ({ load }) =>
      ({
        [keyName]: load[keyName],
      } as Record<K, ILoad[K]>)
  )
}

/**
 * Hook to create and access a dynamic selector that only accesses
 * a single value in ILoad from `loadDetailsSelector`
 *
 * Useful for components that only have one concern, and should not
 * update or re-render for other updates to ILoad state
 *
 * @example const { isExternal } = useLoadSelector('isExternal')
 */
export const useLoadSelector = <K extends keyof ILoad>(keyName: K) => {
  return useSelector(createLoadSelector(keyName))
}
